import { createSlice, current } from "@reduxjs/toolkit";
import { getJobDescriptionThunk, getJobPositionsThunk, getSourcedCandidatesThunk } from "../thunks/InterviewThunk";
import { getQualifiedOutreachCandidatesThunk } from "../thunks/FindCandidatesThunk";

/** Label shown in the Source column for rows that came in through outreach. */
export const OUTREACH_SOURCE_LABEL = 'Outreach';

/**
 * Shape a /sourcing/contacted row into what CommonTable, the selection reducers and
 * the scheduling services expect. The two vocabularies differ (candidate_id/full_name/
 * email/responded_at vs _id/name/candidate_email/applied_at), so the sourcing fields
 * are kept alongside the mapped ones — ScreeningBreakdownModal still needs
 * responses/score_breakdown.
 *
 * Applied here, as the rows land in the store, rather than in the table: every reducer
 * in this file looks candidates up by `_id`, so raw sourcing rows were invisible to all
 * of them — selection, select-all, sorting and post-schedule removal all silently did
 * nothing on the Outreached tab.
 *
 * `fallbackJobId` / `fallbackJobRole` cover rows the sourcing service doesn't stamp
 * with a job: under a single-job scope the scope itself is the answer.
 */
export const toAtsRow = (c, fallbackJobId, fallbackJobRole) => {
  const phone =
    c.phone || c.professional_phone || c.personal_phone || c.mobile || '';
  return {
    ...c,
    _id: c.candidate_id,
    unique_id: c.candidate_id,
    name: c.full_name || 'Unknown',
    candidate_email: c.email || '',
    contact_number: phone,
    mobile: phone,
    job_role: c.job_role || fallbackJobRole || '',
    job_id: c.job_id || fallbackJobId || null,
    // The date column reads applied_at; for an outreached candidate the meaningful
    // date is when they replied.
    applied_at: c.responded_at || null,
    // No resume was scanned, so there is no resume score — the column is hidden for
    // this tab and the cell renders a dash if it is switched back on.
    score: null,
    source_of_application: OUTREACH_SOURCE_LABEL,
    _tableSource: 'OUTREACHED',
  };
};

const TAB_SUBTAB_MAP = {
  SOURCED: ["ALL", "MY_UPLOADS", "SOURCED_CANDIDATES", "OUTREACHED"],
  INTERVIEW: ["ALL", "SCHEDULED", "PROGRESS", "COMPLETED"],
  DECISION: ["ALL", "ACCEPTED", "REJECTED"],
};

const getInitialTab = () => {
  const stored = localStorage.getItem("activeTab");
  if (stored && Object.keys(TAB_SUBTAB_MAP).includes(stored)) {
    return stored;
  }
  return "SOURCED";
};

const getInitialSubTab = (tab) => {
  const stored = localStorage.getItem("activeSubTab");
  const validSubs = TAB_SUBTAB_MAP[tab];
  if (stored && validSubs.includes(stored)) {
    return stored;
  }
  return validSubs[0]; // default first sub-tab
};

const getInitialNavigationTab = () => {
  const stored = localStorage.getItem("navigationTab");
  if (stored && ["ALL", "SOURCED", "INTERVIEW"].includes(stored)) {
    return stored;
  }
  return "ALL";
};

const initialTab = getInitialTab();



const initialState = {
  activeTab: initialTab,
  activeSubTab: getInitialSubTab(initialTab),
  activeSubSubTab: localStorage.getItem("activeSubSubTab") || "All",
  navigationTab: getInitialNavigationTab(),

  // Data Lists
  allScreenedCandidates: [],
  shortListedCandidates: [],
  retrofitCandidates: [],
  invalidCandidates: [],
  sourcedCandidates: [],
  sourcedShortlistedCandidates: [],
  sourcedRetrofitCandidates: [],
  // Candidates who replied to an outreach sequence and passed its screening gates.
  // Fetched from the sourcing service, not derived from a resume score, so it has no
  // Shortlisted/Retrofit split — every row here is already qualified.
  outreachedCandidates: [],

  // Metadata & State
  notifications: [],
  selectionCount: 0,
  resume_score_threshold: 0,
  total_candidates: 0,
  total_uploads: 0,

  // Modals & Selection
  isReportModalOpen: false,
  analysisReport: {},
  currentCandidate: null,
  selectedJobDescription: null,
  isJdModelOpen: false,
  jdLoading: false,
  jdError: null,
  loading: false,
  outreachedLoading: false,
  selectedCandidates: [],
  error: null,
  selectAllClicked: false,
  currentSortDir: "",
  isSchedulePopupOpen: false,
  candidatesLoading: false,
  sortConfig: { key: null, direction: null },
};


export const candidateManagementSlice = createSlice({
  name: "candidateManagementSlice",
  initialState,
  reducers: {
    updateActiveTab: (state, action) => {
      const tab = action.payload;
      state.selectedCandidates = []
      state.selectionCount = 0;
      state.selectAllClicked = false;
      if (Object.keys(TAB_SUBTAB_MAP).includes(tab)) {
        state.activeTab = tab;
        state.activeSubTab = TAB_SUBTAB_MAP[tab][0];
        state.selectedCandidates = []
        state.selectionCount = 0;
        state.selectAllClicked = false;
        localStorage.setItem("activeTab", tab);
        localStorage.setItem("activeSubTab", TAB_SUBTAB_MAP[tab][0]);
      }
    },

    updateActiveSubTab: (state, action) => {
      const subTab = action.payload;
      const allowed = TAB_SUBTAB_MAP[state.activeTab];
      state.selectAllClicked = false;
      state.selectedCandidates = [];
      if (allowed.includes(subTab)) {
        state.activeSubTab = subTab;
        state.selectionCount = 0;
        state.selectAllClicked = false;
        state.selectedCandidates = [];
        state.selectAllClicked = false;
        // OUTREACHED has no Shortlisted/Retrofit/Invalid split — the screening gates
        // already decided, so it stays on "All" rather than landing on a stage that
        // would filter every row out.
        const defaultSubSubTab = subTab === "OUTREACHED" ? "All" : "Shortlisted";
        if (subTab != "ALL") {
          state.activeSubSubTab = defaultSubSubTab;
          localStorage.setItem("activeSubSubTab", defaultSubSubTab);
        }
        localStorage.setItem("activeSubTab", subTab);
      }
    },

    updateActiveSubSubTab: (state, action) => {
      state.activeSubSubTab = action.payload;
      state.selectionCount = 0;
      state.selectAllClicked = false;
      state.selectedCandidates = [];
      state.selectAllClicked = false
      localStorage.setItem("activeSubSubTab", action.payload);
    },

    updateScreenedCandidatesData: (state, action) => {
      const { notifications, all_candidates, resume_score_threshold, total_candidates, total_uploads } = action.payload;
      // console.log({PAYLOAD:action.payload})
      if (notifications.length > 0) {
        state.notifications = notifications;
      }
      if (all_candidates.length > 0) {
        state.allScreenedCandidates = all_candidates;
        state.total_candidates = total_candidates;
      }
      if (resume_score_threshold >= 0) {
        state.resume_score_threshold = resume_score_threshold;
      }
      state.total_uploads = total_uploads;
    },

    updateSourcedCandidatesData: (state, action) => {
      const sourcedCandidates = action.payload;
      if (sourcedCandidates.length >= 0) {
        state.sourcedCandidates = sourcedCandidates;
      }
    },

    updateCandidateCatagory: (state, action) => {
      const { candidates, catagory } = action.payload;
      if (candidates.length >= 0 && catagory === "SHORTLISTED") {
        state.shortListedCandidates = candidates;
        return;
      }
      if (candidates.length >= 0 && catagory === "RETROFIT") {
        state.retrofitCandidates = candidates;
        return;
      }
      if (candidates.length >= 0 && catagory === "INVALID") {
        state.invalidCandidates = candidates;
        return;

      }
    },

    updateSourcedCandidateCatagory: (state, action) => {
      const { candidates, catagory } = action.payload;
      if (candidates.length >= 0 && catagory === "SHORTLISTED") {
        state.sourcedShortlistedCandidates = candidates;
        return;
      }
      if (candidates.length >= 0 && catagory === "RETROFIT") {
        state.sourcedRetrofitCandidates = candidates;
        return;
      }
    },

    clearCandidateCategoryData: (state, action) => {
      state.shortListedCandidates = [];
      state.retrofitCandidates = [];
      state.invalidCandidates = [];
    },

    toggleReportModalState: (state, action) => {
      state.isReportModalOpen = !state.isReportModalOpen;
      state.analysisReport = {};
      state.currentCandidate = null;
    },

    generateAnalysisReport: (state, action) => {
      const { candidate } = action.payload;
      if (!candidate) {
        return;
      }
      state.currentCandidate = candidate;
      state.analysisReport = {
        score: candidate.score,
        experience_match: candidate.experience_match || [],
        location_match: candidate.location_match || {},
        education_match: candidate.education_match || {},
        tech_tool_match: candidate.tech_tool_match || [],
        red_flags: candidate.red_flags || [],
        Strengths: candidate.report?.Strengths || [],
        Weaknesses: candidate.report?.Weaknesses || [],
        Suggestions: candidate.report?.Suggestions || [],
        name: candidate.name,
      }
      state.isReportModalOpen = true;
    },

    toggleJdModelState: (state, _) => {
      state.isJdModelOpen = !state.isJdModelOpen;
    },

    updateCandidateSelectionOld: (state, action) => {
      const { candidateId, value } = action.payload;

      const lists = [
        state.shortListedCandidates,
        state.retrofitCandidates,
        state.sourcedShortlistedCandidates,
        state.sourcedRetrofitCandidates,
      ];
      let foundCnt = 0;
      lists.forEach(list => {
        const candidate = list.find(c => c._id === candidateId);
        if (candidate) {
          if (candidate.isSelected !== value) {
            candidate.isSelected = value;
            state.selectionCount += value ? 1 : -1;
          }
          return;
        }
      });
    },
    updateCandidateSelection: (state, action) => {
      const { candidateId, value } = action.payload;

      const lists = [
        state.shortListedCandidates,
        state.retrofitCandidates,
        state.sourcedShortlistedCandidates,
        state.sourcedRetrofitCandidates,
        state.outreachedCandidates,
      ];

      let targetCandidate = null;

      for (const list of lists) {
        const candidate = list.find(c => c._id === candidateId);
        if (candidate) {
          targetCandidate = candidate;
          break; // ✅ actually stops
        }
      }

      if (!targetCandidate) return;
      if (targetCandidate.isSelected === value) return;

      targetCandidate.isSelected = value;

      if (value) {
        if (!state.selectedCandidates.some(c => c._id === candidateId)) {
          state.selectedCandidates.push(targetCandidate);
        }
      } else {
        state.selectedCandidates = state.selectedCandidates.filter(
          c => c._id !== candidateId
        );
        state.selectAllClicked = false
      }

      state.selectionCount = state.selectedCandidates.length;
    },


    updateCandidateEmailOld: (state, action) => {
      const { candidate, activeSubTab, activeSubSubTab } = action.payload;
      if (!candidate || !activeSubSubTab || !activeSubTab) {
        return;
      }
      if (activeSubTab === "MY_UPLOADS" && activeSubSubTab === "Shortlisted") {
        const index = state.shortListedCandidates.findIndex(
          (c) => c._id === candidate._id
        );

        if (index !== -1) {
          state.shortListedCandidates[index].candidate_email =
            candidate.candidate_email;
        }
      }
      if (activeSubTab === "MY_UPLOADS" && activeSubSubTab === "Retrofit") {
        const index = state.retrofitCandidates.findIndex(
          (c) => c._id === candidate._id
        );

        if (index !== -1) {
          state.retrofitCandidates[index].candidate_email =
            candidate.candidate_email;
        }
      }
    },

    updateCandidateEmail: (state, action) => {
      const { candidate, activeSubTab, activeSubSubTab, field = "candidate_email" } = action.payload;
      if (!candidate || !activeSubTab) return;

      const updateInList = (list) => {
        const index = list.findIndex((c) => c._id === candidate._id);
        if (index !== -1) {
          // Update the specific field
          list[index][field] = candidate[field];

          // Sync mobile and contact_number for UI consistency
          if (field === "mobile" || field === "contact_number") {
            const newVal = candidate.mobile || candidate.contact_number;
            list[index].mobile = newVal;
            list[index].contact_number = newVal;
          }
          return true;
        }
        return false;
      };

      if (activeSubTab === "ALL") {
        // Search both lists — we don't know which one the candidate belongs to
        updateInList(state.shortListedCandidates);
        updateInList(state.retrofitCandidates);
        updateInList(state.invalidCandidates);
        updateInList(state.sourcedShortlistedCandidates);
        updateInList(state.sourcedRetrofitCandidates);
        return;
      }

      if (activeSubTab === "MY_UPLOADS") {
        if (activeSubSubTab === "Shortlisted") updateInList(state.shortListedCandidates);
        if (activeSubSubTab === "Retrofit") updateInList(state.retrofitCandidates);
      }

      if (activeSubTab === "SOURCED_CANDIDATES") {
        if (activeSubSubTab === "Shortlisted") updateInList(state.sourcedShortlistedCandidates);
        if (activeSubSubTab === "Retrofit") updateInList(state.sourcedRetrofitCandidates);
        return;
      }
    },


    updateSelectAllCandidateReducer: (state, action) => {
      const { activeSubTab, activeSubSubTab, value, candidates = [] } = action.payload;

      let list = [];
      console.log({ activeSubTab, activeSubSubTab, value, candidates })
      // 🔹 ALL TAB — combined list passed via candidates prop
      if (activeSubTab === "ALL") {
        list = candidates.map(c => ({ ...c }));
        console.log({ list })
      }

      // 🔹 MY UPLOADS
      if (activeSubTab === "MY_UPLOADS") {
        if (activeSubSubTab === "Shortlisted") {
          list = state.shortListedCandidates;
        } else if (activeSubSubTab === "Retrofit") {
          list = state.retrofitCandidates;
        } else if (activeSubSubTab === "Invalid") {
          list = state.invalidCandidates;
        }
      }

      // 🔹 SOURCED CANDIDATES
      if (activeSubTab === "SOURCED_CANDIDATES") {
        if (activeSubSubTab === "Shortlisted") {
          list = state.sourcedShortlistedCandidates;
        } else if (activeSubSubTab === "Retrofit") {
          list = state.sourcedRetrofitCandidates;
        }
      }

      // 🔹 OUTREACHED — one list, no stage split
      if (activeSubTab === "OUTREACHED") {
        list = state.outreachedCandidates;
      }

      // Scope MY_UPLOADS / SOURCED / OUTREACHED select-all to the current page.
      // `candidates` holds the page's rows.
      if (
        activeSubTab === "MY_UPLOADS" ||
        activeSubTab === "SOURCED_CANDIDATES" ||
        activeSubTab === "OUTREACHED"
      ) {
        const candidateKey = (c) => c._id || c.unique_id || c.candidate_email;
        const pageKeys = new Set(candidates.map(candidateKey));
        list = list.filter(c => pageKeys.has(candidateKey(c)));
      }

      // 🔹Interview Tabs
      if (
        activeSubTab === "SCHEDULED" ||
        activeSubTab === "PROGRESS" ||
        activeSubTab === "COMPLETED"
      ) {
        // Copying Data Cause getting it from other slice and changing other slice data from one slice is not allowed
        list = candidates.map(c => ({ ...c }));
      }

      // 🔹Decision Tabs
      if (activeSubTab === "ACCEPTED") {
        list = candidates.map(c => ({ ...c }));
      }

      if (activeSubTab === "REJECTED") {
        list = candidates.map(c => ({ ...c }));
      }

      // Nothing to act on — leave the header checkbox unticked rather than showing it
      // checked over an empty selection.
      if (!list || list.length === 0) {
        state.selectAllClicked = false;
        state.selectionCount = 0;
        state.selectedCandidates = [];
        return;
      }

      state.selectAllClicked = value;
      if (value) {
        // ✅ SELECT ALL
        list.forEach(candidate => {
          candidate.isSelected = true;

          const exists = state.selectedCandidates.some(c => c._id === candidate._id);
          if (!exists) {
            state.selectedCandidates.push(candidate);
          }

          // ✅ Also update isSelected in the original source lists
          if (activeSubTab === "ALL") {
            const allLists = [
              state.shortListedCandidates,
              state.retrofitCandidates,
              state.sourcedShortlistedCandidates,
              state.sourcedRetrofitCandidates,
            ];
            for (const sourceList of allLists) {
              const original = sourceList.find(c => c._id === candidate._id);
              if (original) {
                original.isSelected = true;
                break;
              }
            }
          }
        });
      } else {
        // ✅ UNSELECT ALL
        console.log("This Is Called")
        list.forEach(candidate => {
          candidate.isSelected = false;
          if (activeSubTab === "ALL") {
            const allLists = [
              state.shortListedCandidates,
              state.retrofitCandidates,
              state.sourcedShortlistedCandidates,
              state.sourcedRetrofitCandidates,
            ];
            for (const sourceList of allLists) {
              const original = sourceList.find(c => c._id === candidate._id);
              if (original) {
                original.isSelected = false;
                break;
              }
            }
          }
        });

        const idsToRemove = new Set(list.map(c => c._id));

        state.selectedCandidates = state.selectedCandidates.filter(
          c => !idsToRemove.has(c._id)
        );
      }

      state.selectionCount = state.selectedCandidates.length;
    },

    unselectAllCandidatesReducer: (state, action) => {
      const { activeSubTab, activeSubSubTab, candidates = [] } = action.payload;

      state.selectAllClicked = false;

      let list = [];



      // 🔹 MY UPLOADS
      if (activeSubTab === "MY_UPLOADS") {
        if (activeSubSubTab === "Shortlisted") {
          list = state.shortListedCandidates;
        } else if (activeSubSubTab === "Retrofit") {
          list = state.retrofitCandidates;
        } else if (activeSubSubTab === "Invalid") {
          list = state.invalidCandidates;
        }
      }

      // 🔹 SOURCED CANDIDATES
      if (activeSubTab === "SOURCED_CANDIDATES") {
        if (activeSubSubTab === "Shortlisted") {
          list = state.sourcedShortlistedCandidates;
        } else if (activeSubSubTab === "Retrofit") {
          list = state.sourcedRetrofitCandidates;
        }
      }

      // 🔹 OUTREACHED
      if (activeSubTab === "OUTREACHED") {
        list = state.outreachedCandidates;
      }

      // 🔹 INTERVIEW TABS
      if (
        activeSubTab === "SCHEDULED" ||
        activeSubTab === "PROGRESS" ||
        activeSubTab === "COMPLETED"
      ) {
        list = candidates.map(c => ({ ...c }));
      }

      if (!list || list.length === 0) {
        state.selectionCount = 0;
        state.selectedCandidates = [];
        return;
      }

      // ✅ UNSELECT ALL
      list.forEach(candidate => {
        candidate.isSelected = false;
      });

      const idsToRemove = new Set(list.map(c => c._id));

      state.selectedCandidates = state.selectedCandidates.filter(
        c => !idsToRemove.has(c._id)
      );

      state.selectionCount = state.selectedCandidates.length;
    },

    sortTableByNameReducer: (state, action) => {
      const { activeSubTab, activeSubSubTab, value } = action.payload;
      // value: "ASC" | "DSC"

      let list = [];

      // 🔹 MY UPLOADS
      if (activeSubTab === "MY_UPLOADS") {
        if (activeSubSubTab === "Shortlisted") {
          list = state.shortListedCandidates;
        } else if (activeSubSubTab === "Retrofit") {
          list = state.retrofitCandidates;
        } else if (activeSubSubTab === "Invalid") {
          list = state.invalidCandidates;
        }
      }

      // 🔹 SOURCED CANDIDATES
      if (activeSubTab === "SOURCED_CANDIDATES") {
        if (activeSubSubTab === "Shortlisted") {
          list = state.sourcedShortlistedCandidates;
        } else if (activeSubSubTab === "Retrofit") {
          list = state.sourcedRetrofitCandidates;
        }
      }


      if (!list || list.length <= 1) return;

      if (value === "ASC") {
        state.currentSortDir = "ASC";
      } else {
        state.currentSortDir = "DSC";
      }

      list.sort((a, b) => {
        const nameA = (a.name || "").toLowerCase();
        const nameB = (b.name || "").toLowerCase();

        if (value === "ASC") {
          return nameA.localeCompare(nameB);
        }
        if (value === "DSC") {
          return nameB.localeCompare(nameA);
        }
        return 0;
      });
    },

    toggleSchedulePopupState: (state, action) => {
      state.isSchedulePopupOpen = !state.isSchedulePopupOpen
    },

    removeSelectedCandidates: (state, action) => {
      const idsToRemove = new Set(
        action.payload?.length
          ? action.payload
          : state.selectedCandidates.map(c => c._id)
      );

      // 🔹 SOURCED CANDIDATES
      if (state.activeSubTab === "SOURCED_CANDIDATES") {
        state.sourcedShortlistedCandidates =
          state.sourcedShortlistedCandidates.filter(
            c => !idsToRemove.has(c._id)
          );

        state.sourcedRetrofitCandidates =
          state.sourcedRetrofitCandidates.filter(
            c => !idsToRemove.has(c._id)
          );
      }
      // 🔹 OUTREACHED
      else if (state.activeSubTab === "OUTREACHED") {
        state.outreachedCandidates =
          state.outreachedCandidates.filter(
            c => !idsToRemove.has(c._id)
          );
      }
      // 🔹 MY UPLOADS
      else {
        state.shortListedCandidates =
          state.shortListedCandidates.filter(
            c => !idsToRemove.has(c._id)
          );

        state.retrofitCandidates =
          state.retrofitCandidates.filter(
            c => !idsToRemove.has(c._id)
          );

        state.invalidCandidates =
          state.invalidCandidates.filter(
            c => !idsToRemove.has(c._id)
          );
      }

      // ✅ Reset selection
      state.selectedCandidates = [];
      state.selectionCount = 0;
      state.selectAllClicked = false;
    },
    removeCandidateFromSelectedList: (state, action) => {
      const candidate = action.payload;

      if (candidate) {

        const existsBefore = state.selectedCandidates.some(
          (c) => c._id === candidate._id
        );

        if (!existsBefore) {
          console.warn(
            `[removeCandidateFromSelectedList] Candidate NOT found in selectedCandidates. ID: ${candidate._id}, Name: ${candidate.name}`
          );
          return;
        }
        state.selectAllClicked = false;
        state.selectedCandidates = state.selectedCandidates.filter(
          (c) => c._id !== candidate._id
        );
        state.selectionCount = state.selectedCandidates.length;

        console.log(
          `[removeCandidateFromSelectedList] ✅ Removed candidate. ID: ${candidate._id}, Name: ${candidate.name} | Remaining count: ${state.selectionCount}`
        );
      } else {
        console.warn('[removeCandidateFromSelectedList] Called with null/undefined candidate — skipping.');
      }
    },

    addcandidateToSelectedList: (state, action) => {
      const candidate = action.payload;
      if (!candidate) return;

      const exists = state.selectedCandidates.some(c => c._id === candidate._id);
      if (exists) {
        state.selectedCandidates = state.selectedCandidates.filter(
          c => c._id !== candidate._id
        );
        state.selectAllClicked = false;
      } else {
        state.selectedCandidates.push(candidate);
      }
      state.selectionCount = state.selectedCandidates.length;
    },

    toggleCandidateloading: (state, action) => {
      state.candidatesLoading = !state.candidatesLoading
    },

    setCandidateLoading: (state, action) => {
      state.loading = action.payload; // true or false
    },

    clearCandidateManagementSlice: () => initialState,

    updateNavigationTab: (state, action) => {
      const tab = action.payload;
      if (["ALL", "SOURCED", "INTERVIEW"].includes(tab)) {
        state.navigationTab = tab;
        localStorage.setItem("navigationTab", tab); // ✅ persist

        if (tab === "SOURCED") {
          state.activeTab = "SOURCED";
          state.activeSubTab = "ALL";
          localStorage.setItem("activeTab", "SOURCED");
          localStorage.setItem("activeSubTab", "ALL");
        }
        if (tab === "INTERVIEW") {
          state.activeTab = "INTERVIEW";
          state.activeSubTab = "ALL";
          localStorage.setItem("activeTab", "INTERVIEW");
          localStorage.setItem("activeSubTab", "ALL");
        }
        state.selectedCandidates = [];
        state.selectionCount = 0;
        state.selectAllClicked = false;
      }
    },

    sortTableByNameReducer: (state, action) => {
      const { activeSubTab, activeSubSubTab, key, direction } = action.payload;
      // key: 'name' | 'score' | 'applied_at' etc.
      // direction: 'asc' | 'desc' | null

      // ✅ Always update sortConfig first
      state.sortConfig = { key, direction };

      if (!key || !direction) return;

      let list = [];

      if (activeSubTab === "MY_UPLOADS") {
        if (activeSubSubTab === "Shortlisted") list = state.shortListedCandidates;
        else if (activeSubSubTab === "Retrofit") list = state.retrofitCandidates;
        else if (activeSubSubTab === "Invalid") list = state.invalidCandidates;
      }
      if (activeSubTab === "SOURCED_CANDIDATES") {
        if (activeSubSubTab === "Shortlisted") list = state.sourcedShortlistedCandidates;
        else if (activeSubSubTab === "Retrofit") list = state.sourcedRetrofitCandidates;
      }
      if (activeSubTab === "OUTREACHED") {
        list = state.outreachedCandidates;
      }

      if (!list || list.length <= 1) return;

      const getValue = (candidate, key) => {
        switch (key) {
          case 'score':
            const score = Number(candidate?.score);
            return isNaN(score) ? null : score;
          case 'applied_at':
          case 'bulk_upload_date':
            const date = Date.parse(candidate?.[key]);
            return isNaN(date) ? null : date;
          // CommonTable's date header sorts on 'date' — the underlying field differs
          // per row source (uploads carry uploaded_on, everything else applied_at).
          case 'date': {
            const field =
              candidate?._tableSource === 'MY_UPLOADS'
                ? candidate?.uploaded_on
                : candidate?.applied_at;
            const parsed = Date.parse(field);
            return isNaN(parsed) ? null : parsed;
          }
          default:
            return candidate?.[key]?.toString().toLowerCase() ?? null;
        }
      };

      list.sort((a, b) => {
        const valA = getValue(a, key);
        const valB = getValue(b, key);

        if (valA === null && valB === null) return 0;
        if (valA === null) return 1;
        if (valB === null) return -1;

        if (typeof valA === 'number') {
          return direction === 'asc' ? valA - valB : valB - valA;
        }

        if (valA < valB) return direction === 'asc' ? -1 : 1;
        if (valA > valB) return direction === 'asc' ? 1 : -1;
        return 0;
      });

      state.currentSortDir = direction === 'asc' ? 'ASC' : 'DSC';
    },

    toggleSelectAllState: (state, action) => {
      state.selectAllClicked = action.value;
    }
  },
  extraReducers: (builder) => {
    // You can add extra reducers here if needed in the future
    builder.addCase(getSourcedCandidatesThunk.pending, (state, action) => {
      state.loading = true;
      state.error = null;
    });
    builder.addCase(getSourcedCandidatesThunk.fulfilled, (state, action) => {
      const sourcedCandidates = action.payload;
      if (sourcedCandidates.length >= 0) {
        state.sourcedCandidates = sourcedCandidates;
      }
      state.loading = false;
      state.error = null;
    });
    builder.addCase(getSourcedCandidatesThunk.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
      console.error("Failed to fetch sourced candidates:", action.payload);
    });
    /* ── Outreached (qualified) candidates ─────────────────────────────────── */
    builder.addCase(getQualifiedOutreachCandidatesThunk.pending, (state) => {
      state.outreachedLoading = true;
      state.error = null;
    });
    builder.addCase(getQualifiedOutreachCandidatesThunk.fulfilled, (state, action) => {
      // Normalise here so the store holds one shape. The job fallbacks come from the
      // scope this fetch ran under (meta.arg), which is re-dispatched whenever the
      // scope changes — so the rows always carry the job they were read for.
      const { jobId = null, jobRole = '' } = action.meta?.arg ?? {};
      state.outreachedCandidates = (action.payload || []).map((c) =>
        toAtsRow(c, jobId, jobRole),
      );
      state.outreachedLoading = false;
      state.error = null;
    });
    builder.addCase(getQualifiedOutreachCandidatesThunk.rejected, (state, action) => {
      // Superseded by a newer request (job scope changed) — not a failure to report.
      if (action.meta?.aborted) return;
      state.outreachedLoading = false;
      state.error = action.payload;
      console.error("Failed to fetch outreached candidates:", action.payload);
    });
    builder.addCase(getJobDescriptionThunk.pending, (state, action) => {
      state.jdLoading = true;
      state.jdError = null;
    });
    builder.addCase(getJobDescriptionThunk.fulfilled, (state, action) => {
      const description = action.payload
      if (!description) {
        console.log("Description Not Found in action.payload");
        return;
      }
      state.selectedJobDescription = description;
    });
    builder.addCase(getJobPositionsThunk.rejected, (state, action) => {
      state.jdError = action.payload;
      state.loading = false;
    })
  }
});


export const { updateActiveTab, updateActiveSubTab, updateScreenedCandidatesData, updateCandidateCatagory, clearCandidateCategoryData, updateSourcedCandidateCatagory, toggleReportModalState, generateAnalysisReport, toggleJdModelState, updateActiveSubSubTab, updateCandidateSelection, updateCandidateEmail, updateSelectAllCandidateReducer, sortTableByNameReducer, toggleSchedulePopupState, removeSelectedCandidates, addcandidateToSelectedList, clearCandidateManagementSlice, toggleCandidateloading, updateNavigationTab, unselectAllCandidatesReducer, removeCandidateFromSelectedList, setCandidateLoading, toggleSelectAllState } = candidateManagementSlice.actions;
